import React, { useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, useMediaQuery, PaletteMode } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import ChatInterface from './components/ChatInterface';

// Function to create theme based on mode
const getTheme = (mode: PaletteMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#2196f3', // Blue for patron messages
      dark: '#1976d2',
      light: '#64b5f6',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#ff9800', // Orange for restaurant branding
      dark: '#f57c00',
      light: '#ffb74d',
      contrastText: '#ffffff'
    },
    background: mode === 'light' ? {
      default: '#f5f5f5', // Light gray background
      paper: '#ffffff'
    } : {
      default: '#121212', // Dark background
      paper: '#1e1e1e' // Slightly lighter for cards
    },
    text: mode === 'light' ? {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#bdbdbd'
    } : {
      primary: '#ffffff',
      secondary: '#b0b0b0',
      disabled: '#666666'
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e'
    },
    success: {
      main: '#4caf50'
    },
    warning: {
      main: '#ff9800'
    },
    error: {
      main: '#f44336'
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
      fontSize: 'clamp(1rem, 3.5vw, 1.2rem)' // Responsive to user text size
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: 'clamp(0.95rem, 3vw, 1.1rem)' // Responsive to user text size
    },
    body1: {
      fontSize: 'clamp(0.875rem, 2.5vw, 1.2rem)', // Fixed: 2.5vw ensures 14px minimum is used
      lineHeight: 1.5
    },
    body2: {
      fontSize: 'clamp(1rem, 3.5vw, 1.1rem)', // Industry standard: min 16px
      lineHeight: 1.4
    },
    caption: {
      fontSize: 'clamp(0.75rem, 3vw, 0.9rem)', // Industry standard: min 12px
      lineHeight: 1.3
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        html: {
          // Respect user's device text size preferences
          fontSize: '100%', // This allows the browser/OS to control base font size
          textSizeAdjust: '100%', // Prevents automatic text scaling
          WebkitTextSizeAdjust: '100%',
          // PWA fullscreen optimizations
          height: '100%',
          // Handle status bar area on iOS
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
          margin: 0
        },
        body: {
          margin: 0,
          padding: 0,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: theme.palette.background.default,
          // Ensure text scales with user preferences
          fontSize: 'inherit', // Inherit from html element
          // PWA fullscreen optimizations
          height: '100%',
          overflow: 'hidden', // Prevent scrolling on body in PWA mode
          // Disable text selection on mobile for better UX
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          KhtmlUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          userSelect: 'none',
          // Re-enable text selection for input areas
          '& input, & textarea': {
            WebkitUserSelect: 'text',
            KhtmlUserSelect: 'text',
            MozUserSelect: 'text',
            msUserSelect: 'text',
            userSelect: 'text'
          }
        },
        '#root': {
          height: '100vh',
          overflow: 'hidden'
        },
        // Disable pull-to-refresh on mobile
        'body, html': {
          overscrollBehavior: 'none'
        }
      })
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '20px',
          fontWeight: 500
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  }
});

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 1
    }
  }
});

const App: React.FC = () => {
  // Detect user's device dark mode preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Create theme based on device preference
  const theme = useMemo(
    () => getTheme(prefersDarkMode ? 'dark' : 'light'),
    [prefersDarkMode]
  );

  // iOS-specific fix: Prevent elastic scrolling/bouncing
  useEffect(() => {
    // Only apply to iOS devices
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    if (isIOS) {
      
      // Prevent body from scrolling (iOS Safari workaround)
      const preventBodyScroll = (e: TouchEvent) => {
        // Only prevent if touching the body or root, not scrollable content
        const target = e.target as HTMLElement;
        const scrollableContainer = target.closest('[data-scrollable="true"]');
        
        // If not in a scrollable container, prevent the bounce
        if (!scrollableContainer) {
          e.preventDefault();
        }
      };
      
      // Add passive: false to allow preventDefault
      document.body.addEventListener('touchmove', preventBodyScroll, { passive: false });
      
      // Also lock document to prevent overscroll
      document.addEventListener('touchmove', (e) => {
        if (e.target === document.documentElement || e.target === document.body) {
          e.preventDefault();
        }
      }, { passive: false });
      
      // iOS KEYBOARD FIX: Debounced viewport stabilization with manual safe-area compensation
      // Since env(safe-area-inset-*) only works in PWA mode, we manually detect and apply offsets
      let lastHeight = window.visualViewport?.height || window.innerHeight;
      let keyboardWasOpen = false;
      let stabilizationTimer: NodeJS.Timeout | null = null;
      
      const applyLayout = (isKeyboardOpen: boolean) => {
        if (isKeyboardOpen) {
          console.log('🍎 iOS: Keyboard opened - clearing ALL bottom padding');
          // Remove ALL safe area padding while keyboard is open
          const root = document.documentElement;
          const body = document.body;
          const container = document.getElementById('root')?.firstElementChild as HTMLElement;
          const mainContainer = container?.querySelector('[data-scrollable="true"]')?.parentElement as HTMLElement;
          
          // Clear top padding
          root.style.paddingTop = '0px';
          root.style.paddingBottom = '0px';
          body.style.paddingBottom = '0px';
          
          if (container) {
            container.style.top = '0px';
            container.style.paddingBottom = '0px';
            container.style.marginBottom = '0px';
          }
          
          if (mainContainer) {
            mainContainer.style.paddingBottom = '0px';
            mainContainer.style.marginBottom = '0px';
          }
          
          // Also clear any MessageInput parent containers
          const messageInputContainer = document.querySelector('[class*="MessageInput"]')?.parentElement as HTMLElement;
          if (messageInputContainer) {
            messageInputContainer.style.paddingBottom = '0px';
            messageInputContainer.style.marginBottom = '0px';
          }
          
          console.log('✅ Cleared all bottom padding/margins');
        } else {
          console.log('🍎 iOS: Keyboard closed - applying safe areas');

          // Calculate safe area offsets from viewport/screen difference
          const screenHeight = window.screen.height;
          const viewportHeight = window.innerHeight;
          const visualViewportHeight = window.visualViewport?.height || window.innerHeight;
          
          // Top safe area (notch) - iPhone 12/13/14/15 typically 40-47px
          const topSafeArea = Math.min(Math.max(screenHeight - viewportHeight, 0), 50);
          
          // Bottom safe area (home indicator + any URL bar) - typically 20-34px
          const bottomSafeArea = Math.min(Math.max(viewportHeight - visualViewportHeight, 0), 40);
          
          // Apply the safe area padding
          const root = document.documentElement;
          const container = document.getElementById('root')?.firstElementChild as HTMLElement;
          const mainContainer = container?.querySelector('[data-scrollable="true"]')?.parentElement as HTMLElement;
          
          // Apply top padding for notch
          if (topSafeArea > 0) {
            root.style.paddingTop = `${topSafeArea}px`;
          } else {
            // Fallback for devices without detectable safe area
            root.style.paddingTop = '40px';
          }
          
          // Apply bottom padding for home indicator
          if (bottomSafeArea > 0) {
            root.style.paddingBottom = `${bottomSafeArea}px`;
            if (mainContainer) {
              mainContainer.style.paddingBottom = `${bottomSafeArea}px`;
            }
          } else {
            // Fallback for home indicator area
            const fallbackBottom = 20;
            root.style.paddingBottom = `${fallbackBottom}px`;
            if (mainContainer) {
              mainContainer.style.paddingBottom = `${fallbackBottom}px`;
            }
          }
          
          if (container) {
            container.style.top = '0px';
          }
        }
      };
      
      const handleViewportResize = () => {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const heightDiff = lastHeight - currentHeight;
        
        // Clear any pending stabilization timer
        if (stabilizationTimer) {
          clearTimeout(stabilizationTimer);
        }
        
        // Keyboard opened - height decreased significantly
        if (heightDiff > 150 && !keyboardWasOpen) {
          keyboardWasOpen = true;
          applyLayout(true);
        } 
        // Keyboard closed - height increased significantly
        else if (heightDiff < -150 && keyboardWasOpen) {
          keyboardWasOpen = false;
          
          // DEBOUNCE: Wait for viewport to stabilize before applying fix
          stabilizationTimer = setTimeout(() => {
            applyLayout(false);
          }, 150); // Wait 150ms after last resize event
        }
        
        lastHeight = currentHeight;
      };
      
      // Listen for viewport changes (keyboard open/close)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportResize);
      } else {
        window.addEventListener('resize', handleViewportResize);
      }
      
      return () => {
        document.body.removeEventListener('touchmove', preventBodyScroll);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportResize);
        } else {
          window.removeEventListener('resize', handleViewportResize);
        }
      };
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ChatInterface />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
